import { useRef, type KeyboardEvent } from 'react';
import { cn } from '../../lib/utils.ts';
import { Tooltip } from './tooltip.tsx';
import { SEG_COUNT_ACTIVE, TAB_COUNT_INACTIVE, SEG_TRACK, tabStateClass } from './tab-style.ts';
import { radioKeyTarget } from './radio-keys.ts';

/**
 * Zielfeld einer Taste im Segment: wie `radioKeyTarget`, aber gesperrte Felder werden übersprungen – bei Pfeil links/oben
 * und Ende rückwärts, sonst vorwärts. `null` = Taste gehört nicht dazu oder nichts wählbar.
 */
export function segmentKeyTarget(key: string, index: number, options: readonly { disabled?: boolean }[]): number | null {
  const target = radioKeyTarget(key, index, options.length);
  if (target === null) return null;
  const step = key === 'ArrowLeft' || key === 'ArrowUp' || key === 'End' ? -1 : 1;
  let next = target;
  for (let n = 0; options[next]!.disabled && n < options.length; n++) next = (next + step + options.length) % options.length;
  return options[next]!.disabled ? null : next;
}

/**
 * Reiter-Segment als Auswahl (DESIGN `components.segment`): graue Spur, aktive Wahl dunkelblau – die einzige Fassung für
 * jede Umschalt-Leiste (Rudolf 21.10.2026, „alles einheitlich“; Wächter `test/segment-einheitlich.test.ts`).
 * Bedienung wie eine Radiogruppe: ein Tab-Stopp, Pfeiltasten wechseln die Wahl. Optional erklärt `hint` ein Wort in
 * einem Satz als Tooltip (Maus und Tastaturfokus; z. B. Feldregeln „Aus Shopware übernehmen“, EXPERIENCE §4.10).
 */
export function Segment<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
  dense,
  testId,
  busy,
  className,
}: {
  label: string;
  /** `count` (opt-in, Ticket 1.247): Zähler hinter dem Namen wie bei den Ansichten-Reitern (Entwurf D05). */
  options: readonly { value: T; label: string; disabled?: boolean; hint?: string; count?: number; testId?: string }[];
  /** `null` = nichts gewählt (z. B. Zustand „Abweichend“); dann ist die erste Möglichkeit per Tab erreichbar. */
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Schmale Spalten (z. B. Chatter): weniger Innenabstand je Reiter. */
  dense?: boolean;
  /** `data-testid` der Gruppe. */
  testId?: string;
  /** Während des Speicherns: Wahl gesperrt, aber `aria-disabled` statt `disabled`, damit der Fokus am Knopf bleibt (Ticket 1.245). */
  busy?: boolean;
  className?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const index = Math.max(0, options.findIndex((o) => o.value === value));

  function onKeyDown(e: KeyboardEvent) {
    if (busy) return;
    const next = segmentKeyTarget(e.key, index, options);
    if (next === null) return;
    e.preventDefault();
    onChange(options[next]!.value);
    refs.current[next]?.focus();
  }

  return (
    <div role="radiogroup" aria-label={label} onKeyDown={onKeyDown} data-testid={testId} className={cn(SEG_TRACK, 'max-w-full', className)}>
      {options.map((o, i) => {
        const active = o.value === value;
        const button = (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active || (value === null && i === 0) ? 0 : -1}
            disabled={disabled || o.disabled}
            aria-disabled={busy || undefined}
            onClick={() => {
              if (!busy) onChange(o.value);
            }}
            data-testid={o.testId}
            className={cn(
              // Knopf 8/12 (DESIGN „Segment“, Ticket 1.271); Zeilenhöhe 16 px hält den Knopf 32 px hoch; Touch mind. 44 × 44 px.
              'cursor-pointer rounded-full py-space-2 leading-4 pointer-coarse:min-h-11 pointer-coarse:min-w-11 text-md whitespace-nowrap transition-colors disabled:cursor-default aria-disabled:cursor-default',
              dense ? 'px-space-2' : 'px-space-3',
              tabStateClass(active, { seg: true }),
            )}
          >
            {o.label}
            {o.count === undefined ? null : <span className={cn('ml-space-1 tabular-nums', active ? SEG_COUNT_ACTIVE : TAB_COUNT_INACTIVE)}>{o.count}</span>}
          </button>
        );
        return o.hint ? (
          <Tooltip key={o.value} label={o.hint} side="bottom" wrap>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </div>
  );
}
